<template>
  <ul role="list" class="mt-auto space-y-1" v-if="isBottom">
    <li v-for="route in routes">
      <RouterLink :to="{ name: route.route }" :target="route.external ? '_blank' : 'target'" :class="[isActive(route.prefix), 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']">
        <component :is="route.icon" :class="[isActiveIcon(route.prefix), 'size-6 shrink-0']"/>
        {{ route.name }}
      </RouterLink>
    </li>
  </ul>

  <li v-else>
    <div class="text-xs/6 font-semibold text-gray-400" v-if="isGrouped">{{ title }}</div>
    <ul role="list" class="-mx-2 mt-2 space-y-1">

      <template v-if="isGrouped">
        <li v-for="route in routes">
          <RouterLink :to="{ name: route.route }" :class="[isActive(route.prefix), 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']">
            <span class="flex size-6 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-[0.625rem] font-medium text-gray-400" v-if="(typeof route.icon === 'string')">
              {{ route.icon }}
            </span>
            <component :is="route.icon" :class="[isActiveIcon(route.prefix), 'size-6 shrink-0']" v-else/>
            <span class="truncate">{{ route.name }}</span>
            <span v-if="route.open" class="ml-auto w-9 min-w-max rounded-full px-2.5 py-0.5 text-center text-xs/5 font-medium whitespace-nowrap bg-white ring-gray-200 text-gray-600 ring-1 ring-inset">{{ route.open }}</span>
          </RouterLink>
        </li>
      </template>

      <li v-for="route in routes" v-else>
        <RouterLink v-if="!route.children" :to="{ name: route.route }" :class="[isActive(route.prefix), 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']">
          <component :is="route.icon" :class="[isActiveIcon(route.prefix), 'size-6 shrink-0']"/>
          {{ route.name }}
        </RouterLink>

        <div v-else>
          <button @click="toggle(route.prefix)" type="button" :class="[isActive(route.prefix), 'flex hover:cursor-pointer w-full items-center gap-x-3 rounded-md p-2 text-left text-sm/6 font-semibold text-gray-700']">
            <component :is="route.icon" class="size-6 shrink-0"/>
            {{ route.name }}
            <ChevronRightIcon class="ml-auto text-gray-400 size-5 shrink-0 ease-in transition-transform" :class="{ 'rotate-90': isCollapsed(route.prefix) }"/>
          </button>

          <SlideTransition is-vertical>
            <ul class="mt-1 pl-8 space-y-1" id="sub-menu-1" v-if="isCollapsed(route.prefix)">
              <RouterLink is="li" v-for="childRoute in route.children" :to="{ name: childRoute.route }" :class="[isActive(childRoute.prefix), 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']">
                <component :is="childRoute.icon" :class="[isActiveIcon(childRoute.prefix), 'size-6 shrink-0']"/>
                {{ childRoute.name }}
              </RouterLink>
            </ul>
          </SlideTransition>
        </div>
      </li>

    </ul>
  </li>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import type { Route } from '@/interfaces'
import { useCollapseState } from '@/composables'
import { SlideTransition } from '@component/Transitions'
import { ChevronRightIcon } from '@heroicons/vue/24/outline'

const route = useRoute();
const { isCollapsed, toggle } = useCollapseState("sidebar-collapse-map");

defineProps<{
  readonly routes: Route[];
  readonly title?: string;
  readonly isBottom?: boolean;
  readonly isGrouped?: boolean;
}>();

function isActive(prefix: string): string {
  const isActive = route.name?.toString().includes(prefix);

  return isActive ? "bg-gray-100 text-indigo-600" : "text-gray-700 hover:text-indigo-600 hover:bg-gray-100";
}

function isActiveIcon(prefix: string): string {
  const isActive = route.name?.toString().includes(prefix);

  return isActive ? "text-indigo-600 border-indigo-600" : "text-gray-700 group-hover:border-indigo-600 group-hover:text-indigo-600";
}
</script>